import React, { useState, useMemo, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Search, Plus, Download, Copy, Printer, Columns, Edit, Trash2, Loader2 } from 'lucide-react'
import toast from 'react-hot-toast'
import { showDeleteConfirm, showSuccess } from '@/lib/swal'
import { PageHeader } from '@/components/shared/PageHeader'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { useAuthStore } from '@/store/auth.store'
import { incentiveService } from '@/services/incentiveService'
import { employeesService } from '@/services/employeesService'
import type { IncentiveListItem } from '@/api/types/incentive.types'
import type { Employee } from '@/api/types'

function getDefaultMonthYear(): string {
  const d = new Date()
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
}

function formatMonthYear(ym?: string, locale?: string): string {
  if (!ym || ym.length < 7) return '—'
  const [y, m] = ym.split('-')
  const monthNum = parseInt(m, 10)
  if (monthNum >= 1 && monthNum <= 12 && y) {
    const loc = locale?.toLowerCase().startsWith('ar') ? 'ar-SA' : 'en-US'
    return new Date(Number(y), monthNum - 1, 1).toLocaleDateString(loc, { month: 'long', year: 'numeric' })
  }
  return ym
}

/** Format created date as date only — locale-aware */
function formatCreatedDate(dateString?: string, locale?: string): string {
  if (!dateString) return '—'
  try {
    const loc = locale?.toLowerCase().startsWith('ar') ? 'ar-SA' : 'en-US'
    const d = new Date(dateString)
    return d.toLocaleDateString(loc, { year: 'numeric', month: 'short', day: '2-digit' })
  } catch {
    return String(dateString).slice(0, 10) || '—'
  }
}

export const IncentiveList: React.FC = () => {
  const { t, i18n } = useTranslation('common')
  const locale = i18n.language
  const navigate = useNavigate()
  const { user } = useAuthStore()
  const branchId = user?.selectedBranch?.branchId

  const [rows, setRows] = useState<IncentiveListItem[]>([])
  const [loading, setLoading] = useState(false)
  const [monthYear, setMonthYear] = useState(getDefaultMonthYear)
  const [search, setSearch] = useState('')
  const [pageNumber, setPageNumber] = useState(1)
  const [pageSize] = useState(10)
  const [totalRecords, setTotalRecords] = useState(0)
  const [columnVisibility, setColumnVisibility] = useState<Record<string, boolean>>({
    employee: true,
    monthYear: true,
    amount: true,
    createdDate: true,
    action: true,
  })
  const [employeesList, setEmployeesList] = useState<Employee[]>([])
  const [extraNames, setExtraNames] = useState<Record<number, string>>({})
  const fetchedEmpIdsRef = useRef<Set<number>>(new Set())

  const columnVisibilityLabel = (key: string): string => {
    switch (key) {
      case 'employee':
        return t('incentivePage.colEmployee')
      case 'monthYear':
        return t('incentivePage.colMonthYear')
      case 'amount':
        return t('incentivePage.colAmount')
      case 'createdDate':
        return t('incentivePage.colCreatedDate')
      case 'action':
        return t('incentivePage.colAction')
      default:
        return key
    }
  }

  useEffect(() => {
    if (!branchId) {
      setRows([])
      setTotalRecords(0)
      setEmployeesList([])
      setExtraNames({})
      fetchedEmpIdsRef.current = new Set()
      return
    }
    let cancelled = false
    setLoading(true)
    incentiveService
      .getAll(branchId, pageNumber, pageSize)
      .then((res) => {
        if (!cancelled) {
          setRows(res.data ?? [])
          setTotalRecords(res.totalRecords ?? 0)
        }
      })
      .catch((err: any) => {
        if (!cancelled) {
          setRows([])
          setTotalRecords(0)
          const msg = err.response?.data?.message || err.message || t('incentivePage.fetchFailed')
          toast.error(msg)
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => { cancelled = true }
  }, [branchId, pageNumber, pageSize])

  // Same employee list as Incentive form – for showing name in list by empId
  useEffect(() => {
    if (!branchId) return
    let cancelled = false
    employeesService.getAllEmployees(branchId, 1, 1000).then((res: any) => {
      if (cancelled) return
      const raw = res?.data ?? res?.Data
      const list = Array.isArray(raw) ? raw : (raw?.data ?? raw?.Data ?? [])
      setEmployeesList(Array.isArray(list) ? list : [])
    }).catch(() => { if (!cancelled) setEmployeesList([]) })
    return () => { cancelled = true }
  }, [branchId])

  const employeeOptions = useMemo(() => {
    const list = Array.isArray(employeesList) ? employeesList : []
    return list.map((e: any) => {
      const id = e.id ?? e.Id ?? e.employeeId ?? e.empId ?? e.EmpId
      const first = e.empFirstNameEn ?? e.EmpFirstNameEn ?? ''
      const family = e.empFamilyNameEn ?? e.EmpFamilyNameEn ?? e.empLastNameEn ?? e.EmpLastNameEn ?? ''
      const label = ([first, family].filter(Boolean).join(' ').trim()) || (e.empCode ?? e.EmpCode ?? '—')
      return { value: id != null ? String(id) : '', label }
    }).filter((o) => o.value !== '')
  }, [employeesList])

  // Fallback: agar empId list mein na ho to getEmployeeById se naam lao
  useEffect(() => {
    if (!branchId || rows.length === 0) return
    rows.forEach((r) => {
      const eid = r.employeeId != null ? Number(r.employeeId) : null
      if (eid == null || eid <= 0) return
      if (employeeOptions.some((o) => o.value === String(eid))) return
      if (extraNames[eid]) return
      if (fetchedEmpIdsRef.current.has(eid)) return
      fetchedEmpIdsRef.current.add(eid)
      employeesService.getEmployeeById(branchId, eid).then((emp: any) => {
        const first = emp?.empFirstNameEn ?? emp?.EmpFirstNameEn ?? ''
        const family = emp?.empFamilyNameEn ?? emp?.EmpFamilyNameEn ?? emp?.empLastNameEn ?? emp?.EmpLastNameEn ?? ''
        const name = [first, family].filter(Boolean).join(' ').trim() || (emp?.empCode ?? emp?.EmpCode ?? '—')
        setExtraNames((prev) => ({ ...prev, [eid]: name }))
      }).catch(() => {})
    })
  }, [branchId, rows, employeeOptions, extraNames])

  const getEmployeeDisplayName = (row: IncentiveListItem): string => {
    if (row.employeeName) return row.employeeName
    const eid = row.employeeId != null ? Number(row.employeeId) : null
    if (eid == null) return '—'
    const fromOptions = employeeOptions.find((o) => o.value === String(eid))?.label
    if (fromOptions) return fromOptions
    return extraNames[eid] ?? '—'
  }

  const filteredRows = useMemo(() => {
    if (!search.trim()) return rows
    const q = search.toLowerCase()
    return rows.filter(
      (r) =>
        String(r.id).toLowerCase().includes(q) ||
        (r.employeeName ?? '').toLowerCase().includes(q) ||
        (r.monthYear ?? '').toLowerCase().includes(q)
    )
  }, [rows, search])

  const totalPages = Math.max(1, Math.ceil(totalRecords / pageSize))
  const visibleColumnCount =
    Object.values(columnVisibility).filter(Boolean).length || 1
  const pagFrom = totalRecords === 0 ? 0 : (pageNumber - 1) * pageSize + 1
  const pagTo = totalRecords === 0 ? 0 : Math.min(pageNumber * pageSize, totalRecords)

  const handleDelete = async (row: IncentiveListItem) => {
    if (!row.id) return
    const ok = await showDeleteConfirm(undefined, t('incentivePage.deleteConfirm'))
    if (!ok) return
    try {
      await incentiveService.delete(row.id)
      await showSuccess(t('incentivePage.deletedTitle'), t('incentivePage.deletedMessage'), 2000)
      setRows((prev) => prev.filter((r) => r.id !== row.id))
      setTotalRecords((prev) => Math.max(0, prev - 1))
    } catch (err: any) {
      toast.error(err.response?.data?.message || t('incentivePage.deleteFailed'))
    }
  }

  const handleCsv = () => toast.success(t('incentivePage.csvStub'))
  const handleCopy = () => toast.success(t('incentivePage.copyStub'))
  const handlePrint = () => window.print()

  if (!branchId) {
    return (
      <div className="space-y-6 p-4 sm:p-6">
        <PageHeader title={t('incentivePage.pageTitle')} description={t('incentivePage.pageDescription')} />
        <Card>
          <CardContent className="py-8 text-center text-muted-foreground">
            {t('incentivePage.selectBranchList')}
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="space-y-6 p-4 sm:p-6">
      <PageHeader title={t('incentivePage.pageTitle')} description={t('incentivePage.pageDescription')}>
        <Button variant="default" onClick={() => navigate('/payroll/incentives/new')}>
          <Plus className="w-4 h-4 mr-2" />
          {t('incentivePage.addIncentive')}
        </Button>
      </PageHeader>

      <Card>
        <CardHeader>
          <CardTitle>{t('incentivePage.cardTitle')}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">
                {t('incentivePage.requiredMark')} {t('incentivePage.labelMonthYearFilter')}
              </label>
              <Input
                type="month"
                value={monthYear}
                onChange={(e) => setMonthYear(e.target.value)}
                className="w-full"
              />
            </div>
          </div>
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="relative w-64">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder={t('incentivePage.searchPlaceholder')}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9"
              />
            </div>
            <div className="flex items-center gap-2">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm">
                    <Columns className="w-4 h-4 mr-2" />
                    {t('incentivePage.columnVisibility')}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48">
                  {Object.entries(columnVisibility).map(([key, visible]) => (
                    <DropdownMenuCheckboxItem
                      key={key}
                      checked={visible}
                      onCheckedChange={(checked) =>
                        setColumnVisibility((prev) => ({ ...prev, [key]: !!checked }))
                      }
                    >
                      {columnVisibilityLabel(key)}
                    </DropdownMenuCheckboxItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
              <Button variant="outline" size="sm" onClick={handleCsv}>
                <Download className="w-4 h-4 mr-2" />
                {t('incentivePage.csv')}
              </Button>
              <Button variant="outline" size="sm" onClick={handleCopy}>
                <Copy className="w-4 h-4 mr-2" />
                {t('incentivePage.copy')}
              </Button>
              <Button variant="outline" size="sm" onClick={handlePrint}>
                <Printer className="w-4 h-4 mr-2" />
                {t('incentivePage.print')}
              </Button>
            </div>
          </div>
          <div className="rounded-md border overflow-x-auto">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    {columnVisibility.employee && <TableHead className="text-center">{t('incentivePage.colEmployee')}</TableHead>}
                    {columnVisibility.monthYear && <TableHead className="text-center">{t('incentivePage.colMonthYear')}</TableHead>}
                    {columnVisibility.amount && <TableHead className="text-center">{t('incentivePage.colAmount')}</TableHead>}
                    {columnVisibility.createdDate && <TableHead className="text-center">{t('incentivePage.colCreatedDate')}</TableHead>}
                    {columnVisibility.action && <TableHead className="text-center">{t('incentivePage.colAction')}</TableHead>}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredRows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={visibleColumnCount} className="text-center text-muted-foreground py-8">
                        {t('incentivePage.noData')}
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredRows.map((row) => (
                      <TableRow key={row.id}>
                        {columnVisibility.employee && (
                          <TableCell className="text-center">
                            {getEmployeeDisplayName(row)}
                          </TableCell>
                        )}
                        {columnVisibility.monthYear && <TableCell className="text-center">{formatMonthYear(row.monthYear, locale)}</TableCell>}
                        {columnVisibility.amount && <TableCell className="text-center">{row.amount ?? '—'}</TableCell>}
                        {columnVisibility.createdDate && <TableCell className="text-center">{formatCreatedDate(row.createdDate, locale)}</TableCell>}
                        {columnVisibility.action && (
                          <TableCell className="text-center">
                            <div className="flex items-center justify-center gap-1">
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => navigate(`/payroll/incentives/${row.id}/edit`)}
                                title={t('incentivePage.editTitle')}
                              >
                                <Edit className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => handleDelete(row)}
                                title={t('incentivePage.deleteTitle')}
                              >
                                <Trash2 className="h-4 w-4 text-destructive" />
                              </Button>
                            </div>
                          </TableCell>
                        )}
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            )}
          </div>
          <div className="flex flex-wrap items-center justify-between gap-4 text-sm text-muted-foreground">
            <span>
              {t('incentivePage.paginationShowing', { from: pagFrom, to: pagTo, total: totalRecords })}
            </span>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={pageNumber <= 1}
                onClick={() => setPageNumber((p) => Math.max(1, p - 1))}
              >
                {t('incentivePage.previous')}
              </Button>
              <span>
                {t('incentivePage.pageOf', { page: pageNumber, totalPages })}
              </span>
              <Button
                variant="outline"
                size="sm"
                disabled={pageNumber >= totalPages}
                onClick={() => setPageNumber((p) => p + 1)}
              >
                {t('incentivePage.next')}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
